import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { MessageCircle, X, Send, Plus, Trash2, Sparkles, Bot, User, ChevronLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  student: 'Study Buddy',
  teacher: 'Teaching Assistant',
  parent: 'Parent Guide',
  institute_admin: 'Business Advisor',
  super_admin: 'Platform Assistant',
};

const ROLE_PLACEHOLDERS = {
  student: 'Ask any doubt — Math, Science, English...',
  teacher: 'Ask about lesson plans, teaching tips...',
  parent: "Ask about your child's progress...",
  institute_admin: 'Ask about enrollment, fees, growth...',
  super_admin: 'Ask about platform management...',
};

const QUICK_PROMPTS = {
  student: ['Explain quadratic equations', 'Tips for exam preparation', 'How to improve English writing?'],
  teacher: ['Create a lesson plan for fractions', 'How to engage students in class?', 'Assessment ideas for Science'],
  parent: ['How to help my child study better?', 'What is a good study routine?', 'How to motivate my child?'],
  institute_admin: ['How to increase student enrollment?', 'Fee collection strategies', 'How to retain students?'],
  super_admin: ['Platform growth strategies', 'Institute onboarding tips', 'Analytics best practices'],
};

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatInline(text) {
  // First escape unsafe HTML entities to eliminate XSS
  const safe = escapeHtml(text);
  // Then parse safe markdown inline styles into semantic tags
  return safe
    .replace(/`([^`]+)`/g, '<code class="bg-slate-200/80 px-1 py-0.5 rounded text-xs font-mono text-indigo-700 font-semibold">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold text-slate-900">$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em class="italic">$1</em>');
}

function MarkdownText({ text }) {
  if (!text) return null;
  const lines = String(text).split('\n');
  return (
    <div className="space-y-1.5">
      {lines.map((line, i) => {
        if (!line.trim()) return <br key={i} />;
        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          const content = line.trim().slice(2);
          return (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-indigo-400 mt-0.5 select-none">•</span>
              <span dangerouslySetInnerHTML={{ __html: formatInline(content) }} />
            </div>
          );
        }
        if (/^\d+\.\s/.test(line.trim())) {
          const num = line.trim().match(/^(\d+)\.\s/)[1];
          const content = line.trim().replace(/^\d+\.\s/, '');
          return (
            <div key={i} className="flex gap-2 items-start">
              <span className="text-indigo-400 font-semibold min-w-[20px] select-none">{num}.</span>
              <span dangerouslySetInnerHTML={{ __html: formatInline(content) }} />
            </div>
          );
        }
        if (line.trim().startsWith('###')) return <p key={i} className="font-semibold text-slate-800 mt-2 dark:text-slate-100" dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^###\s*/, '')) }} />;
        if (line.trim().startsWith('##')) return <p key={i} className="font-bold text-slate-900 mt-2 dark:text-white" dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^##\s*/, '')) }} />;
        if (line.trim().startsWith('#')) return <p key={i} className="font-bold text-lg text-slate-900 mt-2 dark:text-white" dangerouslySetInnerHTML={{ __html: formatInline(line.replace(/^#\s*/, '')) }} />;
        return <p key={i} dangerouslySetInnerHTML={{ __html: formatInline(line) }} />;
      })}
    </div>
  );
}

// hideTrigger: no floating button on pages whose own controls live in that corner (Live Chat's Send)
export default function AIChatWidget({ hideTrigger = false }) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const role = user?.role || 'student';
  const label = ROLE_LABELS[role] || 'AI Assistant';
  const placeholder = ROLE_PLACEHOLDERS[role] || 'Type your message...';
  const quickPrompts = QUICK_PROMPTS[role] || [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/ai/chat/sessions`);
      setSessions(res.data || []);
    } catch { /* ignore */ }
  };

  const loadHistory = async (sid) => {
    try {
      const res = await axios.get(`${API}/ai/chat/history`, { params: { session_id: sid } });
      const msgs = [];
      (res.data || []).forEach(m => {
        msgs.push({ role: 'user', content: m.user_message });
        msgs.push({ role: 'ai', content: m.ai_response });
      });
      setMessages(msgs);
      setSessionId(sid);
      setShowSessions(false);
    } catch { /* ignore */ }
  };

  const sendMessage = async (textToSend) => {
    const text = (textToSend || input).trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    const activeSessionId = sessionId || `session_${Date.now()}`;
    if (!sessionId) setSessionId(activeSessionId);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: text,
        session_id: activeSessionId,
      });
      const aiReply = res.data?.response || 'I am sorry, I could not generate a response.';
      setMessages(prev => [...prev, { role: 'ai', content: aiReply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', content: 'AI service is temporarily busy. Please try again in a moment.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(`session_${Date.now()}`);
    setShowSessions(false);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/ai/chat/session/${sid}`);
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewChat();
      }
    } catch { /* ignore */ }
  };

  const openSession = (sid) => {
    loadHistory(sid);
  };

  return (
    <>
      {/* Floating Trigger Button */}
      {!isOpen && !hideTrigger && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-[calc(4.75rem+env(safe-area-inset-bottom))] md:bottom-4 right-4 z-40 w-14 h-14 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white rounded-full shadow-lg hover:shadow-xl flex items-center justify-center transition-all duration-200 hover:scale-105 active:scale-95 cursor-pointer"
          aria-label="Open CoachHub AI Assistant"
          data-testid="ai-chat-trigger-btn"
        >
          <Sparkles className="w-6 h-6 text-white" />
          <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed inset-0 md:inset-auto md:bottom-4 md:right-4 z-50 w-full md:w-[420px] h-[100dvh] md:h-[620px] md:rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden dark:bg-slate-900 dark:border-slate-700"
          role="dialog"
          aria-modal="true"
          aria-label="CoachHub AI Assistant"
          data-testid="ai-chat-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-violet-600 text-white p-4 flex items-center justify-between flex-shrink-0 shadow-sm">
            <div className="flex items-center gap-3">
              {showSessions && (
                <button
                  onClick={() => setShowSessions(false)}
                  className="p-1.5 rounded-lg hover:bg-white/15 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center cursor-pointer"
                  aria-label="Back to chat"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              )}
              <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center shadow-xs">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-sm font-outfit">CoachHub AI</h3>
                <p className="text-[11px] text-indigo-100">{showSessions ? 'Chat History' : label}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { loadSessions(); setShowSessions(!showSessions); }}
                className="p-2 rounded-lg hover:bg-white/15 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer"
                title="Chat History"
                aria-label="Chat History"
                data-testid="ai-chat-history-btn"
              >
                <MessageCircle className="w-4 h-4" />
              </button>
              <button
                onClick={startNewChat}
                className="p-2 rounded-lg hover:bg-white/15 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer"
                title="New Chat"
                aria-label="New Chat"
                data-testid="ai-chat-new-btn"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 rounded-lg hover:bg-white/15 transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center cursor-pointer"
                title="Close chat"
                aria-label="Close chat"
                data-testid="ai-chat-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Sessions List */}
          {showSessions ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              <button
                onClick={startNewChat}
                className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-indigo-50 text-indigo-700 text-xs sm:text-sm font-bold hover:bg-indigo-100 transition-colors min-h-[44px] cursor-pointer"
                data-testid="ai-new-chat-session"
              >
                <Plus className="w-4 h-4" /> New Conversation
              </button>
              {sessions.map(s => (
                <div
                  key={s.session_id}
                  onClick={() => openSession(s.session_id)}
                  className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl cursor-pointer transition-colors min-h-[44px] ${sessionId === s.session_id ? 'bg-indigo-50/70 border border-indigo-100' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                >
                  <div className="min-w-0 flex-1 pr-2">
                    <p className="text-xs sm:text-sm text-slate-700 font-medium truncate dark:text-slate-200">{s.preview || 'Conversation'}</p>
                    <p className="text-[10px] text-slate-400 font-semibold">{s.message_count} messages</p>
                  </div>
                  <button
                    onClick={(e) => deleteSession(s.session_id, e)}
                    className="p-2 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors flex-shrink-0 min-h-[36px] min-w-[36px] flex items-center justify-center cursor-pointer"
                    aria-label="Delete chat session"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-center text-xs sm:text-sm text-slate-400 py-10">No previous chats recorded</p>
              )}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xs">
                      <Sparkles className="w-7 h-7 text-indigo-500" />
                    </div>
                    <h3 className="font-bold text-slate-900 text-base mb-1 font-outfit dark:text-white">Hello, {user?.name?.split(' ')[0] || 'there'}!</h3>
                    <p className="text-xs text-slate-500 mb-5 dark:text-slate-400">I'm your AI {label.toLowerCase()}. How can I help you today?</p>
                    <div className="space-y-2">
                      {quickPrompts.map((q, i) => (
                        <button
                          key={i}
                          onClick={() => sendMessage(q)}
                          data-testid={`ai-quick-prompt-${i}`}
                          className="block w-full text-left px-3.5 py-2.5 rounded-xl border border-slate-200 text-xs text-slate-700 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-all font-medium cursor-pointer min-h-[44px] dark:border-slate-700 dark:text-slate-200"
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} className={`flex gap-2.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'ai' && (
                      <div className="w-7 h-7 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Bot className="w-4 h-4 text-indigo-600" />
                      </div>
                    )}
                    <div
                      className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-xs sm:text-sm leading-relaxed ${
                        msg.role === 'user'
                          ? 'bg-indigo-600 text-white rounded-br-xs shadow-xs'
                          : 'bg-slate-100 text-slate-800 rounded-bl-xs dark:bg-slate-800 dark:text-slate-100'
                      }`}
                    >
                      {msg.role === 'ai' ? <MarkdownText text={msg.content} /> : msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                ))}

                {loading && (
                  <div className="flex gap-2.5 items-start">
                    <div className="w-7 h-7 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div className="bg-slate-100 rounded-2xl rounded-bl-xs px-4 py-3 shadow-xs dark:bg-slate-800">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input bar */}
              <div className="p-3 border-t border-slate-100 flex-shrink-0 bg-white dark:border-slate-800 dark:bg-slate-900">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    rows={1}
                    data-testid="ai-chat-input"
                    className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2.5 text-xs sm:text-sm outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 max-h-24 leading-relaxed dark:border-slate-700"
                    style={{ minHeight: '44px' }}
                    aria-label="Chat message input"
                  />
                  <button
                    onClick={() => sendMessage()}
                    disabled={!input.trim() || loading}
                    data-testid="ai-chat-send-btn"
                    aria-label="Send message"
                    className="w-11 h-11 bg-indigo-600 text-white rounded-xl flex items-center justify-center hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-xs flex-shrink-0 active:scale-95 cursor-pointer min-h-[44px] min-w-[44px]"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
